import crypto from 'crypto';
import ECKey from 'ec-key';

import generatePublicKey from './generatePublicKey';

export type GreenDotEncryptedCard = {
  salt: string;
  encrypted_data: {
    data: string;
    ephemeral_public_key: string;
    public_key_hash: string;
    ec_version: string;
  };
};

function encryptCardForGreenDot(
  greenDotPublicKey: string,
  cardNumber: string,
  expDate: string,
  cvv: string
): GreenDotEncryptedCard {
  const [expiryMonth, last2digitYear] = expDate.split('/');
  const expiryYear = `20${last2digitYear}`;

  const debitCardData = JSON.stringify({
    accountNumber: cardNumber,
    expiryMonth,
    expiryYear,
    cvv,
  });

  // Chime generates the shared secret w/ GD's permanent public key and Chime's ephemeral public key
  // Keys are generated using ECDH algorithm (Elliptic Curve Diffie-hellman)
  const chimeKeyPair = getEphemeralKeyPair();
  const chimePublicKey = chimeKeyPair.getPublicKey();
  const sharedSecret = getSharedSecret(chimeKeyPair, greenDotPublicKey);

  const salt = window.crypto.randomUUID();
  const nonSalt = process.env.NODE_ENV;
  const algorithmId = Buffer.from('id-aes256-GCM');
  const aesKey = generateAesKey(sharedSecret, salt, nonSalt, algorithmId);

  const debitCardDataBuffer = Buffer.from(debitCardData);
  return encrypt(
    salt,
    debitCardDataBuffer,
    Buffer.from(aesKey, 'hex'),
    chimePublicKey
  );
}

function generateAesKey(
  sharedSecret: Buffer,
  salt: string,
  nonSalt: string,
  algorithmId: Buffer
) {
  const algorithmIdBuffer = Buffer.alloc(1, 13);
  const nonSaltBuffer = Buffer.from(nonSalt);
  const saltBuffer = Buffer.from(salt);
  const saltHashBuffer = crypto
    .createHash('sha256')
    .update(saltBuffer)
    .digest();
  const totalLength =
    algorithmIdBuffer.length +
    algorithmId.length +
    nonSaltBuffer.length +
    saltHashBuffer.length;

  const algorithmSaltBuffer = Buffer.concat(
    [algorithmIdBuffer, algorithmId, nonSaltBuffer, saltHashBuffer],
    totalLength
  );
  return concatKDF(sharedSecret, algorithmSaltBuffer);
}

// concatKDF concatenates <0001><shared secret><other data>
// then computes the SHA-256 digest
// returns: hex-encoded string of the SHA-256 hash
// based on: https://nvlpubs.nist.gov/nistpubs/Legacy/SP/nistspecialpublication800-56ar.pdf#page=47
function concatKDF(sharedSecret: any, otherData: Buffer): string {
  const hash = crypto.createHash('sha256');

  hash.update(Buffer.from([0, 0, 0, 1]));
  hash.update(sharedSecret);
  hash.update(otherData);
  return hash.digest('hex');
}

function encrypt(
  salt: string,
  cardData: any,
  aesKey: Buffer,
  publicKey: Buffer
): GreenDotEncryptedCard {
  const cipher = crypto.createCipheriv('aes-256-gcm', aesKey, Buffer.alloc(16));
  let encrypted = cipher.update(cardData, 'utf8', 'base64');
  encrypted += cipher.final('base64');
  const tag = cipher.getAuthTag();
  const ciphertext = encrypted + Buffer.from(tag).toString('base64');
  const publicKeyHash = crypto
    .createHash('sha256')
    .update(publicKey)
    .digest('base64');

  return {
    salt: salt,
    encrypted_data: {
      data: ciphertext,
      ephemeral_public_key: publicKey.toString('base64'),
      public_key_hash: publicKeyHash,
      ec_version: 'ECv1',
    },
  };
}

function getSharedSecret(chimeKeyPair: any, greenDotPublicKey: string): Buffer {
  // Add pem headers because the key returned by the backend does not include them
  const greenDotPublicKeyPem = generatePublicKey(greenDotPublicKey);

  // Use ec-key to parse the pem file to generate the ECDH public key
  const greenDotECKey = new ECKey(greenDotPublicKeyPem, 'pem');
  const greenDotPublicKeyBuffer = greenDotECKey.publicCodePoint;

  return chimeKeyPair.computeSecret(greenDotPublicKeyBuffer, null, 'hex');
}

function getEphemeralKeyPair() {
  const curve = 'prime256v1';
  const edchKey = crypto.createECDH(curve);
  edchKey.generateKeys();
  return edchKey;
}

export default encryptCardForGreenDot;
